import { SidebarMenuButton, SidebarMenuItem } from '@ibsys/kit';
import { FC, ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
// Импорт минует индекс `@/shared/hooks`: он тянет хуки, завязанные на entities, и на пункте меню,
// который сами entities и используют, это замкнуло бы цикл модулей.
import { useIsSidebarCollapsed } from '@/shared/hooks/useIsSidebarCollapsed';
import { cn } from '@/shared/lib';
import { TruncatedText } from '../TruncatedText';

type Props = {
  /** Адрес, на который ведёт пункт */
  path: string;
  /** Иконка слева от подписи */
  icon: ReactNode;
  /** Подпись пункта */
  label: ReactNode;
  /** Пункт отмечен активным; по умолчанию — когда открыт его адрес или вложенный в него */
  isActive?: boolean;
  className?: string;
};

/**
 * Пункт бокового меню: иконка, подпись и переход по адресу.
 *
 * Единственное место, где задано оформление пунктов сайдбара и сопоставление «адрес → активный
 * пункт», — иначе и правка вида, и правило активности расходятся по статическому меню и
 * динамическим подменю.
 */
export const SidebarNavItem: FC<Props> = ({ path, icon, label, isActive, className }) => {
  const { pathname } = useLocation();
  const isCollapsed = useIsSidebarCollapsed();

  // Раздел остаётся активным на своих вложенных адресах: открытая из него запись — тот же раздел.
  const isPathActive = pathname === path || pathname.startsWith(`${path}/`);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        className={cn(
          'text-sidebar-foreground',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          'active:bg-sidebar-accent active:text-sidebar-accent-foreground',
          'rounded-md py-2 px-2.5',
          'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
          className,
        )}
        isActive={isActive ?? isPathActive}
      >
        <NavLink to={path} className={cn('flex items-center gap-2 w-full', isCollapsed && 'justify-center gap-0')}>
          {icon}
          {!isCollapsed && (
            /* Подпись усекается здесь, а не у потребителей: ширина меню принадлежит раскладке,
               и пункт с длинным названием из данных не вправе её менять. Полное название даёт
               подсказка — и только тогда, когда подпись не поместилась. Свёрнутое меню название
               показывает своим меню-подсказкой (`SidebarFlyoutMenu`), подписи там нет вовсе. */
            <TruncatedText>{label}</TruncatedText>
          )}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
